import React from 'react';

const CustomIcon = ({ 
  type = "png",
  name, 
  size = 20, 
  color = 'success', 
  gradient = null,
  className = '',
  style = {},
  ...props 
}) => {
  
  // Helper function to safely import icon
  const getIconPath = (iconName, iconType) => {
    try {
      if (iconType === "svg") {
        // For SVG, we need to import the file content or use it as an image
        const iconPath = require(`../../assets/icons/svg/${iconName}.svg`);
        return iconPath;
      } else {
        // For PNG, use the existing approach
        const iconPath = require(`../../assets/icons/all/${iconName}.png`);
        return iconPath;
      }
    } catch (error) {
      console.error(`Icon "${iconName}" not found in assets/icons/${iconType === "png" ? "all" : "svg"}/`, error);
      return null;
    }
  };
  
  // Helper function to create gradient styles using theme variables
  const getGradientStyles = (gradient) => {
    if (!gradient) return {};
    
    const gradientTypes = {
      // Theme-based gradients using CSS variables
      'primary': 'var(--header-gradient)',
      'header': 'var(--header-gradient)',
      'success': 'linear-gradient(135deg, var(--theme-success) 0%, var(--bs-success) 100%)',
      'danger': 'linear-gradient(135deg, var(--theme-danger) 0%, var(--bs-danger) 100%)',
      'warning': 'linear-gradient(135deg, var(--theme-warning) 0%, var(--bs-warning) 100%)',
      'info': 'linear-gradient(135deg, var(--theme-info) 0%, var(--bs-info) 100%)',
      
      // Gray scale gradients using theme variables
      'gray-light': 'linear-gradient(135deg, var(--gray-10) 0%, var(--gray-20) 100%)',
      'gray-medium': 'linear-gradient(135deg, var(--gray-20) 0%, var(--gray-40) 100%)',
      'gray-dark': 'linear-gradient(135deg, var(--gray-40) 0%, var(--gray-60) 100%)',
      
      // Text color gradients
      'text': 'linear-gradient(135deg, var(--theme-text) 0%, var(--theme-text-secondary) 100%)',
      'text-inverse': 'linear-gradient(135deg, var(--theme-light-details) 0%, var(--theme-text-secondary) 100%)',
      
      // Surface gradients
      'surface': 'linear-gradient(135deg, var(--theme-surface) 0%, var(--theme-background) 100%)',
      'sidebar': 'linear-gradient(135deg, var(--theme-sidebar) 0%, var(--theme-surface) 100%)',
      
      // Action gradients
      'success-light': 'linear-gradient(135deg, var(--theme-success) 0%, var(--primary-light) 100%)',
      'danger-light': 'linear-gradient(135deg, var(--theme-danger) 0%, var(--theme-warning) 100%)',
      'warning-light': 'linear-gradient(135deg, var(--theme-warning) 0%, var(--theme-warning-dark) 100%)',
      'info-light': 'linear-gradient(135deg, var(--theme-info) 0%, var(--primary-light) 100%)',
      
      // Border and shadow gradients
      'border': 'linear-gradient(135deg, var(--theme-border) 0%, var(--gray-20) 100%)',
      'shadow': 'linear-gradient(135deg, var(--theme-shadow) 0%, transparent 100%)'
    };
    
    const gradientValue = typeof gradient === 'string' 
      ? gradientTypes[gradient] || gradient 
      : gradient;
    
    return {
      background: gradientValue,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      color: 'transparent'
    };
  };

  // Helper function to convert color to CSS filter using theme variables
  const getColorFilter = (color) => {
    if (color === 'currentColor' || !color) return 'none';
    
    // Map theme color names to CSS variables
    const colorMap = {
      // Theme colors
      'primary': 'var(--bs-primary)',
      'secondary': 'var(--bs-secondary)',
      'success': 'var(--theme-success)',
      'danger': 'var(--theme-danger)',
      'warning': 'var(--theme-warning)',
      'info': 'var(--theme-info)',
      
      // Text colors
      'text': 'var(--theme-text)',
      'text-secondary': 'var(--theme-text-secondary)',
      'light-details': 'var(--theme-light-details)',
      'dark-details': 'var(--theme-dark-details)',
      
      // Background colors
      'background': 'var(--theme-background)',
      'surface': 'var(--theme-surface)',
      'sidebar': 'var(--theme-sidebar)',
      
      // Gray scale
      'gray-80': 'var(--gray-80)',
      'gray-60': 'var(--gray-60)',
      'gray-40': 'var(--gray-40)',
      'gray-20': 'var(--gray-20)',
      'gray-10': 'var(--gray-10)',
      
      // UI elements
      'border': 'var(--theme-border)',
      'disabled': 'var(--theme-disabled)',
      
      // Legacy support for common color names
      'white': 'var(--theme-light-details)',
      'black': 'var(--theme-text)',
      'gray': 'var(--gray-40)',
      'grey': 'var(--gray-40)'
    };
    
    const themeColor = colorMap[color.toLowerCase()];
    
    if (themeColor) {
      // For theme colors, we need to use a different approach
      // Since we can't directly use CSS variables in filters, we'll use CSS custom properties
      return `brightness(0) saturate(100%)`;
    }
    
    // For hex colors or other values, use the original approach
    if (color.startsWith('#') || color.startsWith('rgb') || color.startsWith('hsl')) {
      if (color.toLowerCase() === '#ffffff' || color.toLowerCase() === 'white') {
        return 'brightness(0) invert(1)';
      } else if (color.toLowerCase() === '#000000' || color.toLowerCase() === 'black') {
        return 'brightness(0)';
      }
      return 'brightness(0) saturate(100%)';
    }
    
    return 'brightness(0) saturate(100%)';
  };
  
  const iconPath = getIconPath(name, type);
  
  // If icon not found, return a placeholder
  if (!iconPath) {
    return (
      <div 
        className={`icon-placeholder ${className}`}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: '#ccc',
          borderRadius: '2px',
          display: 'inline-block',
          ...style
        }}
        {...props}
      >
        ?
      </div>
    );
  }

  // For SVG icons, render as image with proper styling
  if (type === 'svg') {
    // If iconPath is null, show placeholder
    if (!iconPath) {
      return (
        <div 
          className={`icon-placeholder ${className}`}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            backgroundColor: '#ccc',
            borderRadius: '2px',
            display: 'inline-block',
            ...style
          }}
          {...props}
        >
          ?
        </div>
      );
    }

    // If gradient is specified for SVG, use the same approach as PNG
    if (gradient) {
      const gradientStyles = getGradientStyles(gradient);
      return (
        <div
          className={`gradient-icon-wrapper ${className}`}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            ...style
          }}
          {...props}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              ...gradientStyles
            }}
          />
          <img
            src={iconPath}
            alt={name}
            width={size}
            height={size}
            style={{
              position: 'relative',
              zIndex: 1,
              filter: 'brightness(0) invert(1)',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </div>
      );
    }

    return (
      <img
        src={iconPath}
        alt={name}
        width={size}
        height={size}
        className={className}
        style={{
          filter: color !== 'currentColor' ? getColorFilter(color) : 'none',
          width: `${size}px`,
          height: `${size}px`,
          display: 'block',
          objectFit: 'contain',
          ...style
        }}
        {...props}
      />
    );
  }
  
  // If gradient is specified, wrap the icon in a container with gradient background
  if (gradient) {
    const gradientStyles = getGradientStyles(gradient);
    
    
    // For PNG icons with gradients
    return (
      <div
        className={`gradient-icon-wrapper ${className}`}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          ...style
        }}
        {...props}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            ...gradientStyles
          }}
        />
        <img
          src={iconPath}
          alt={name}
          width={size}
          height={size}
          style={{
            position: 'relative',
            zIndex: 1,
            filter: 'brightness(0) invert(1)',
            objectFit: 'contain',
            display: 'block'
          }}
        />
      </div>
    );
  }
  
  return (
    <img
      src={iconPath}
      alt={name}
      width={size}
      height={size}
      className={className}
      style={{
        filter: getColorFilter(color),
        width: `${size}px`,
        height: `${size}px`,
        display: 'block',
        objectFit: 'contain',
        ...style
      }}
      {...props}
    />
  );
};

export default CustomIcon;